/**
 * "Create your own song with custom lyrics" - the popup behind the music picker's
 * create button (server: /api/songs, song-generator.service.js).
 *
 *   QRSongCreator.open({ context: { type: 'form'|'site', targetId }, lang,
 *                        onUse(value), projectId? })
 *
 * Steps: style -> details -> lyrics -> free preview -> pay ($12) -> pick.
 * Full screen on phones, a centred dialog on desktop (native <dialog>, so
 * focus, Escape and the backdrop are handled by the browser). Everything is
 * saved on the server as the host goes, so closing it, or leaving for Stripe,
 * loses nothing: reopening resumes the same song. A song in progress keeps
 * being checked after the popup closes, and a small notice says when it is
 * ready.
 */
(function () {
    'use strict';
    if (window.QRSongCreator) return;

    var STYLE_IDS = ['romantic-piano', 'string-quartet', 'piano-and-cello', 'acoustic-guitar', 'ambient-folk',
        'harp-and-flute', 'elegant-waltz', 'spanish-guitar', 'bossa-nova', 'jazz-lounge', 'oud-and-qanun',
        'whimsical-woodwinds', 'sitar-and-bansuri', 'cinematic-strings', 'festive-celebration', 'playful-kids',
        'heavy-metal', 'pop-punk', 'trap-beat', 'electro-house', 'funk-disco', 'cumbia-party'];
    var LANGS = ['en', 'es', 'pt', 'fr', 'de', 'he', 'ar'];
    var LANG_NAMES = { en: 'English', es: 'Español', pt: 'Português', fr: 'Français', de: 'Deutsch', he: 'עברית', ar: 'العربية' };
    var OCCASIONS = ['wedding', 'engagement', 'anniversary', 'quinceanera', 'birthday', 'coming_of_age', 'baby', 'other'];

    var T = {
        en: {
            title: 'Create your song', close: 'Close',
            occasionOther: "What is the celebration?", occasionOtherPh: "e.g. Retirement, graduation, housewarming", lyricsChoose: "How would you like your lyrics?", forMe: "✨ Write them for me", forMeSub: "AI writes lyrics from your names and story. You can edit every word.", own: "✍️ I will write my own", ownSub: "Type or paste your own words.", ownPh: "[Verse 1]\nYour first lines…\n\n[Chorus]\nThe part that repeats…", ownNote: "Your own words only: lyrics from existing songs cannot be sung. Tags like [Verse] and [Chorus] help the singer, but are optional.", back: 'Back', next: 'Next', step: 'Step {n} of {t}',
            disclaimer: 'Your choices guide the AI composer. Each version comes out a little different, which is why you get 3 to choose from.',
            styleH: 'Pick a style', styleSub: 'Tap ▶ to hear each one. Your song will have this feel, with a singer.',
            voiceH: 'Who sings?', female: 'Female', male: 'Male', duet: 'Duet',
            detailsH: 'Tell us about it', occasion: 'What are you celebrating?',
            occ: { wedding: 'Wedding', engagement: 'Engagement', anniversary: 'Anniversary', quinceanera: 'Quinceañera', birthday: 'Birthday', coming_of_age: 'Coming of age', baby: 'Baby celebration', other: 'Something else' },
            name1: 'Name to sing', name2: 'Second name (optional)', sounds: 'How does it sound?', soundsHint: 'Only if it is unusual, e.g. "shi-VAWN"',
            addSound: 'Add how it sounds', date: 'Date (optional)', language: 'Song language',
            story: 'A little story (optional)', storyHint: 'How you met, what you love, a family detail…',
            wish: 'A line you would love in the song (optional)',
            needName: 'Please add at least one name.',
            lyricsH: 'Your lyrics', lyricsSub: 'Change any word. The names appear as written; how they sound is handled for you.',
            writing: 'Writing your lyrics…', romantic: 'More romantic', fun: 'More fun', shorter: 'Shorter', rewrite: 'New version',
            previewH: 'Hear your song', previewSub: 'We make your full song and play you the first 30 seconds, free.',
            makePreview: 'Create my free preview', previewsLeft: '{n} free preview(s) left',
            stage1: 'Composing the melody…', stage2: 'Recording the vocals…', stage3: 'Mixing your song…',
            keepWorking: 'This takes about a minute. You can close this and keep editing; we will let you know.',
            previewLabel: '30-second preview', changeLyrics: 'Change the lyrics', another: 'Try another preview',
            loveIt: 'Keep the full song', failed: 'That one did not work out. Please try again.',
            refused: 'Some words could not be sung. Please reword a line and try again.',
            payH: 'Keep your song', price: '$12', payWhat1: '3 full versions of your song to choose from',
            payWhat2: 'One free redo, for example after changing a name', payWhat3: 'Use it as the music on your invitation',
            pay: 'Unlock for $12', paying: 'Opening secure checkout…',
            pickH: 'Your song', pickSub: 'Listen and choose the version you love.', version: 'Version {n}',
            making: 'Being made…', use: 'Use as my music', inUse: 'Added to your invitation',
            redo: 'Make one more version', redosLeft: '{n} redo left', editFirst: 'Edit the lyrics first',
            readyToast: 'Your song is ready', open: 'Open', error: 'Something went wrong. Please try again.',
            signIn: 'Please sign in to create a song.'
        },
        es: {
            title: 'Crea tu canción', close: 'Cerrar',
            occasionOther: "¿Qué celebran?", occasionOtherPh: "p. ej. Jubilación, graduación, inauguración de casa", lyricsChoose: "¿Cómo quieres tu letra?", forMe: "✨ Escríbela por mí", forMeSub: "La IA escribe la letra con sus nombres e historia. Puedes editar cada palabra.", own: "✍️ La escribo yo", ownSub: "Escribe o pega tus propias palabras.", ownPh: "[Verse 1]\nTus primeras líneas…\n\n[Chorus]\nLa parte que se repite…", ownNote: "Solo palabras tuyas: no se pueden cantar letras de canciones existentes. Las etiquetas como [Verse] y [Chorus] ayudan al cantante, pero son opcionales.", back: 'Atrás', next: 'Siguiente', step: 'Paso {n} de {t}',
            disclaimer: 'Tus elecciones guían al compositor con IA. Cada versión sale un poco distinta, por eso recibes 3 para elegir.',
            styleH: 'Elige un estilo', styleSub: 'Toca ▶ para escuchar cada uno. Tu canción tendrá este estilo, con voz.',
            voiceH: '¿Quién canta?', female: 'Mujer', male: 'Hombre', duet: 'Dúo',
            detailsH: 'Cuéntanos', occasion: '¿Qué celebran?',
            occ: { wedding: 'Boda', engagement: 'Compromiso', anniversary: 'Aniversario', quinceanera: 'XV años', birthday: 'Cumpleaños', coming_of_age: 'Mayoría de edad', baby: 'Bebé', other: 'Otra cosa' },
            name1: 'Nombre para cantar', name2: 'Segundo nombre (opcional)', sounds: '¿Cómo suena?', soundsHint: 'Solo si es poco común, p. ej. "shi-VAWN"',
            addSound: 'Indicar cómo suena', date: 'Fecha (opcional)', language: 'Idioma de la canción',
            story: 'Una pequeña historia (opcional)', storyHint: 'Cómo se conocieron, lo que les encanta, un detalle familiar…',
            wish: 'Una frase que te encantaría en la canción (opcional)',
            needName: 'Agrega al menos un nombre.',
            lyricsH: 'Tu letra', lyricsSub: 'Cambia cualquier palabra. Los nombres aparecen como los escribiste; nosotros nos ocupamos de cómo suenan.',
            writing: 'Escribiendo tu letra…', romantic: 'Más romántica', fun: 'Más alegre', shorter: 'Más corta', rewrite: 'Nueva versión',
            previewH: 'Escucha tu canción', previewSub: 'Creamos tu canción completa y te ponemos los primeros 30 segundos, gratis.',
            makePreview: 'Crear mi vista previa gratis', previewsLeft: 'Te quedan {n} vistas previas gratis',
            stage1: 'Componiendo la melodía…', stage2: 'Grabando la voz…', stage3: 'Mezclando tu canción…',
            keepWorking: 'Tarda alrededor de un minuto. Puedes cerrar esto y seguir editando; te avisaremos.',
            previewLabel: 'Vista previa de 30 segundos', changeLyrics: 'Cambiar la letra', another: 'Probar otra vista previa',
            loveIt: 'Quedarme con la canción completa', failed: 'Esa no salió bien. Inténtalo de nuevo.',
            refused: 'Algunas palabras no se pudieron cantar. Cambia una línea e inténtalo de nuevo.',
            payH: 'Quédate con tu canción', price: '12 US$', payWhat1: '3 versiones completas de tu canción para elegir',
            payWhat2: 'Un nuevo intento gratis, por ejemplo tras cambiar un nombre', payWhat3: 'Úsala como música de tu invitación',
            pay: 'Desbloquear por 12 US$', paying: 'Abriendo el pago seguro…',
            pickH: 'Tu canción', pickSub: 'Escucha y elige la versión que más te guste.', version: 'Versión {n}',
            making: 'Creándose…', use: 'Usar como mi música', inUse: 'Añadida a tu invitación',
            redo: 'Crear una versión más', redosLeft: 'Te queda {n} intento', editFirst: 'Editar la letra primero',
            readyToast: 'Tu canción está lista', open: 'Abrir', error: 'Algo salió mal. Inténtalo de nuevo.',
            signIn: 'Inicia sesión para crear una canción.'
        },
        pt: {
            title: 'Crie sua música', close: 'Fechar',
            occasionOther: "O que vocês estão celebrando?", occasionOtherPh: "ex.: Aposentadoria, formatura, chá de casa nova", lyricsChoose: "Como você quer sua letra?", forMe: "✨ Escreva para mim", forMeSub: "A IA escreve a letra com seus nomes e sua história. Você pode editar cada palavra.", own: "✍️ Eu escrevo a minha", ownSub: "Digite ou cole suas próprias palavras.", ownPh: "[Verse 1]\nSuas primeiras linhas…\n\n[Chorus]\nA parte que se repete…", ownNote: "Apenas palavras suas: letras de músicas existentes não podem ser cantadas. Marcações como [Verse] e [Chorus] ajudam o cantor, mas são opcionais.", back: 'Voltar', next: 'Próximo', step: 'Etapa {n} de {t}',
            disclaimer: 'Suas escolhas guiam o compositor com IA. Cada versão sai um pouco diferente, por isso você recebe 3 para escolher.',
            styleH: 'Escolha um estilo', styleSub: 'Toque em ▶ para ouvir cada um. Sua música terá esse estilo, com voz.',
            voiceH: 'Quem canta?', female: 'Feminina', male: 'Masculina', duet: 'Dueto',
            detailsH: 'Conte para nós', occasion: 'O que vocês estão celebrando?',
            occ: { wedding: 'Casamento', engagement: 'Noivado', anniversary: 'Aniversário de casamento', quinceanera: 'Festa de 15 anos', birthday: 'Aniversário', coming_of_age: 'Maioridade', baby: 'Bebê', other: 'Outra coisa' },
            name1: 'Nome para cantar', name2: 'Segundo nome (opcional)', sounds: 'Como se pronuncia?', soundsHint: 'Só se for incomum, ex.: "shi-VAWN"',
            addSound: 'Indicar a pronúncia', date: 'Data (opcional)', language: 'Idioma da música',
            story: 'Uma pequena história (opcional)', storyHint: 'Como se conheceram, o que amam, um detalhe da família…',
            wish: 'Uma frase que você adoraria na música (opcional)',
            needName: 'Adicione pelo menos um nome.',
            lyricsH: 'Sua letra', lyricsSub: 'Mude qualquer palavra. Os nomes aparecem como escritos; cuidamos da pronúncia.',
            writing: 'Escrevendo sua letra…', romantic: 'Mais romântica', fun: 'Mais alegre', shorter: 'Mais curta', rewrite: 'Nova versão',
            previewH: 'Ouça sua música', previewSub: 'Criamos sua música completa e tocamos os primeiros 30 segundos, grátis.',
            makePreview: 'Criar minha prévia grátis', previewsLeft: 'Restam {n} prévias grátis',
            stage1: 'Compondo a melodia…', stage2: 'Gravando a voz…', stage3: 'Mixando sua música…',
            keepWorking: 'Leva cerca de um minuto. Você pode fechar e continuar editando; avisaremos você.',
            previewLabel: 'Prévia de 30 segundos', changeLyrics: 'Mudar a letra', another: 'Tentar outra prévia',
            loveIt: 'Ficar com a música completa', failed: 'Essa não deu certo. Tente novamente.',
            refused: 'Algumas palavras não puderam ser cantadas. Mude uma linha e tente novamente.',
            payH: 'Fique com sua música', price: 'US$ 12', payWhat1: '3 versões completas da sua música para escolher',
            payWhat2: 'Uma nova tentativa grátis, por exemplo após mudar um nome', payWhat3: 'Use como música do seu convite',
            pay: 'Desbloquear por US$ 12', paying: 'Abrindo o pagamento seguro…',
            pickH: 'Sua música', pickSub: 'Ouça e escolha a versão que você ama.', version: 'Versão {n}',
            making: 'Sendo criada…', use: 'Usar como minha música', inUse: 'Adicionada ao seu convite',
            redo: 'Criar mais uma versão', redosLeft: 'Resta {n} tentativa', editFirst: 'Editar a letra antes',
            readyToast: 'Sua música está pronta', open: 'Abrir', error: 'Algo deu errado. Tente novamente.',
            signIn: 'Entre na sua conta para criar uma música.'
        },
        fr: {
            title: 'Créez votre chanson', close: 'Fermer',
            occasionOther: "Que célébrez-vous ?", occasionOtherPh: "ex. Retraite, remise de diplôme, crémaillère", lyricsChoose: "Comment voulez-vous vos paroles ?", forMe: "✨ Écrivez-les pour moi", forMeSub: "L'IA écrit les paroles avec vos prénoms et votre histoire. Vous pouvez tout modifier.", own: "✍️ J'écris les miennes", ownSub: "Tapez ou collez vos propres mots.", ownPh: "[Verse 1]\nVos premières lignes…\n\n[Chorus]\nLe refrain…", ownNote: "Vos propres mots uniquement : les paroles de chansons existantes ne peuvent pas être chantées. Les repères [Verse] et [Chorus] aident le chanteur, mais sont facultatifs.", back: 'Retour', next: 'Suivant', step: 'Étape {n} sur {t}',
            disclaimer: "Vos choix guident le compositeur IA. Chaque version est un peu différente, c'est pourquoi vous en recevez 3.",
            styleH: 'Choisissez un style', styleSub: 'Touchez ▶ pour écouter. Votre chanson aura ce style, avec une voix.',
            voiceH: 'Qui chante ?', female: 'Voix de femme', male: "Voix d'homme", duet: 'Duo',
            detailsH: 'Racontez-nous', occasion: 'Que célébrez-vous ?',
            occ: { wedding: 'Mariage', engagement: 'Fiançailles', anniversary: 'Anniversaire de mariage', quinceanera: 'Quinceañera', birthday: 'Anniversaire', coming_of_age: 'Passage à l’âge adulte', baby: 'Bébé', other: 'Autre chose' },
            name1: 'Prénom à chanter', name2: 'Second prénom (facultatif)', sounds: 'Comment se prononce-t-il ?', soundsHint: 'Seulement s’il est inhabituel, ex. « shi-VAWN »',
            addSound: 'Indiquer la prononciation', date: 'Date (facultatif)', language: 'Langue de la chanson',
            story: 'Une petite histoire (facultatif)', storyHint: 'Votre rencontre, ce que vous aimez, un détail familial…',
            wish: 'Une phrase que vous aimeriez entendre (facultatif)',
            needName: 'Ajoutez au moins un prénom.',
            lyricsH: 'Vos paroles', lyricsSub: 'Modifiez n’importe quel mot. Les prénoms restent tels quels ; nous gérons la prononciation.',
            writing: 'Écriture de vos paroles…', romantic: 'Plus romantique', fun: 'Plus joyeuse', shorter: 'Plus courte', rewrite: 'Nouvelle version',
            previewH: 'Écoutez votre chanson', previewSub: 'Nous créons votre chanson complète et vous faisons écouter les 30 premières secondes, gratuitement.',
            makePreview: 'Créer mon aperçu gratuit', previewsLeft: 'Encore {n} aperçu(s) gratuit(s)',
            stage1: 'Composition de la mélodie…', stage2: 'Enregistrement de la voix…', stage3: 'Mixage de votre chanson…',
            keepWorking: 'Cela prend environ une minute. Vous pouvez fermer et continuer ; nous vous préviendrons.',
            previewLabel: 'Aperçu de 30 secondes', changeLyrics: 'Modifier les paroles', another: 'Essayer un autre aperçu',
            loveIt: 'Garder la chanson complète', failed: 'Celle-ci n’a pas fonctionné. Réessayez.',
            refused: 'Certains mots n’ont pas pu être chantés. Reformulez une ligne et réessayez.',
            payH: 'Gardez votre chanson', price: '12 $ US', payWhat1: '3 versions complètes de votre chanson au choix',
            payWhat2: 'Un nouvel essai gratuit, par exemple après un changement de prénom', payWhat3: 'Utilisez-la comme musique de votre invitation',
            pay: 'Débloquer pour 12 $ US', paying: 'Ouverture du paiement sécurisé…',
            pickH: 'Votre chanson', pickSub: 'Écoutez et choisissez la version que vous préférez.', version: 'Version {n}',
            making: 'En création…', use: 'Utiliser comme musique', inUse: 'Ajoutée à votre invitation',
            redo: 'Créer une autre version', redosLeft: 'Encore {n} essai', editFirst: 'Modifier les paroles d’abord',
            readyToast: 'Votre chanson est prête', open: 'Ouvrir', error: 'Une erreur est survenue. Réessayez.',
            signIn: 'Connectez-vous pour créer une chanson.'
        },
        de: {
            title: 'Ihr eigener Song', close: 'Schließen',
            occasionOther: "Was feiern Sie?", occasionOtherPh: "z. B. Ruhestand, Abschluss, Einweihungsparty", lyricsChoose: "Wie möchten Sie Ihren Text?", forMe: "✨ Für mich schreiben", forMeSub: "Die KI schreibt den Text aus Ihren Namen und Ihrer Geschichte. Sie können jedes Wort ändern.", own: "✍️ Ich schreibe selbst", ownSub: "Tippen oder fügen Sie Ihre eigenen Worte ein.", ownPh: "[Verse 1]\nIhre ersten Zeilen…\n\n[Chorus]\nDer Teil, der sich wiederholt…", ownNote: "Nur Ihre eigenen Worte: Texte bestehender Songs können nicht gesungen werden. Markierungen wie [Verse] und [Chorus] helfen dem Gesang, sind aber optional.", back: 'Zurück', next: 'Weiter', step: 'Schritt {n} von {t}',
            disclaimer: 'Ihre Auswahl leitet den KI-Komponisten. Jede Version klingt etwas anders, deshalb erhalten Sie 3 zur Auswahl.',
            styleH: 'Wählen Sie einen Stil', styleSub: 'Tippen Sie auf ▶, um jeden anzuhören. Ihr Song bekommt diesen Klang, mit Gesang.',
            voiceH: 'Wer singt?', female: 'Frauenstimme', male: 'Männerstimme', duet: 'Duett',
            detailsH: 'Erzählen Sie uns davon', occasion: 'Was feiern Sie?',
            occ: { wedding: 'Hochzeit', engagement: 'Verlobung', anniversary: 'Jahrestag', quinceanera: 'Quinceañera', birthday: 'Geburtstag', coming_of_age: 'Volljährigkeit', baby: 'Baby', other: 'Etwas anderes' },
            name1: 'Name im Song', name2: 'Zweiter Name (optional)', sounds: 'Wie wird er ausgesprochen?', soundsHint: 'Nur wenn ungewöhnlich, z. B. „shi-VAWN“',
            addSound: 'Aussprache angeben', date: 'Datum (optional)', language: 'Sprache des Songs',
            story: 'Eine kleine Geschichte (optional)', storyHint: 'Wie Sie sich kennengelernt haben, was Sie lieben, ein Detail der Familie…',
            wish: 'Eine Zeile, die Sie gern im Song hätten (optional)',
            needName: 'Bitte geben Sie mindestens einen Namen an.',
            lyricsH: 'Ihr Songtext', lyricsSub: 'Ändern Sie jedes Wort. Namen erscheinen wie geschrieben; um die Aussprache kümmern wir uns.',
            writing: 'Ihr Songtext wird geschrieben…', romantic: 'Romantischer', fun: 'Fröhlicher', shorter: 'Kürzer', rewrite: 'Neue Version',
            previewH: 'Hören Sie Ihren Song', previewSub: 'Wir erstellen Ihren ganzen Song und spielen Ihnen die ersten 30 Sekunden kostenlos vor.',
            makePreview: 'Kostenlose Hörprobe erstellen', previewsLeft: 'Noch {n} kostenlose Hörprobe(n)',
            stage1: 'Die Melodie entsteht…', stage2: 'Der Gesang wird aufgenommen…', stage3: 'Ihr Song wird gemischt…',
            keepWorking: 'Das dauert etwa eine Minute. Sie können das Fenster schließen und weiterarbeiten; wir sagen Bescheid.',
            previewLabel: '30-Sekunden-Hörprobe', changeLyrics: 'Text ändern', another: 'Andere Hörprobe',
            loveIt: 'Den ganzen Song behalten', failed: 'Das hat nicht geklappt. Bitte versuchen Sie es erneut.',
            refused: 'Einige Wörter konnten nicht gesungen werden. Formulieren Sie eine Zeile um und versuchen Sie es erneut.',
            payH: 'Behalten Sie Ihren Song', price: '12 US$', payWhat1: '3 vollständige Versionen Ihres Songs zur Auswahl',
            payWhat2: 'Ein kostenloser neuer Versuch, z. B. nach einer Namensänderung', payWhat3: 'Als Musik Ihrer Einladung verwenden',
            pay: 'Für 12 US$ freischalten', paying: 'Sichere Zahlung wird geöffnet…',
            pickH: 'Ihr Song', pickSub: 'Hören Sie rein und wählen Sie Ihre Lieblingsversion.', version: 'Version {n}',
            making: 'Wird erstellt…', use: 'Als meine Musik verwenden', inUse: 'Zu Ihrer Einladung hinzugefügt',
            redo: 'Noch eine Version erstellen', redosLeft: 'Noch {n} Versuch', editFirst: 'Zuerst den Text bearbeiten',
            readyToast: 'Ihr Song ist fertig', open: 'Öffnen', error: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
            signIn: 'Bitte melden Sie sich an, um einen Song zu erstellen.'
        },
        he: {
            title: 'צרו את השיר שלכם', close: 'סגירה',
            occasionOther: "מה חוגגים?", occasionOtherPh: "למשל פרישה, סיום לימודים, חנוכת בית", lyricsChoose: "איך תרצו את המילים?", forMe: "✨ כתבו אותן בשבילי", forMeSub: "הבינה המלאכותית כותבת מילים מהשמות והסיפור שלכם. אפשר לערוך כל מילה.", own: "✍️ אכתוב בעצמי", ownSub: "הקלידו או הדביקו מילים משלכם.", ownPh: "[Verse 1]\nהשורות הראשונות…\n\n[Chorus]\nהפזמון…", ownNote: "רק מילים שלכם: אי אפשר לשיר מילים של שירים קיימים. תגיות כמו [Verse] ו-[Chorus] עוזרות לזמר, אבל אינן חובה.", back: 'חזרה', next: 'הבא', step: 'שלב {n} מתוך {t}',
            disclaimer: 'הבחירות שלכם מכוונות את המלחין מבוסס הבינה המלאכותית. כל גרסה יוצאת קצת שונה, ולכן תקבלו 3 לבחירה.',
            styleH: 'בחרו סגנון', styleSub: 'הקישו ▶ כדי להאזין. השיר שלכם יישמע בסגנון הזה, עם שירה.',
            voiceH: 'מי שר?', female: 'זמרת', male: 'זמר', duet: 'דואט',
            detailsH: 'ספרו לנו', occasion: 'מה חוגגים?',
            occ: { wedding: 'חתונה', engagement: 'אירוסין', anniversary: 'יום נישואים', quinceanera: 'קינסיאנרה', birthday: 'יום הולדת', coming_of_age: 'בגרות', baby: 'לידה', other: 'משהו אחר' },
            name1: 'שם לשיר', name2: 'שם שני (לא חובה)', sounds: 'איך מבטאים?', soundsHint: 'רק אם השם לא נפוץ, למשל "שי-ווֹן"',
            addSound: 'הוסיפו איך מבטאים', date: 'תאריך (לא חובה)', language: 'שפת השיר',
            story: 'סיפור קטן (לא חובה)', storyHint: 'איך הכרתם, מה אתם אוהבים, פרט משפחתי…',
            wish: 'שורה שהייתם רוצים בשיר (לא חובה)',
            needName: 'יש להוסיף לפחות שם אחד.',
            lyricsH: 'המילים שלכם', lyricsSub: 'אפשר לשנות כל מילה. השמות מופיעים כפי שנכתבו; אנחנו דואגים להגייה.',
            writing: 'כותבים את המילים…', romantic: 'יותר רומנטי', fun: 'יותר שמח', shorter: 'קצר יותר', rewrite: 'גרסה חדשה',
            previewH: 'האזינו לשיר', previewSub: 'אנחנו יוצרים את השיר המלא ומשמיעים לכם את 30 השניות הראשונות, בחינם.',
            makePreview: 'צרו לי טעימה בחינם', previewsLeft: 'נותרו {n} טעימות בחינם',
            stage1: 'מלחינים את המנגינה…', stage2: 'מקליטים את השירה…', stage3: 'מערבבים את השיר…',
            keepWorking: 'זה לוקח בערך דקה. אפשר לסגור ולהמשיך לערוך; נודיע לכם.',
            previewLabel: 'טעימה של 30 שניות', changeLyrics: 'שינוי המילים', another: 'טעימה נוספת',
            loveIt: 'אני רוצה את השיר המלא', failed: 'זה לא הצליח. נסו שוב.',
            refused: 'חלק מהמילים לא ניתנו לשירה. נסחו שורה מחדש ונסו שוב.',
            payH: 'השיר שלכם לתמיד', price: '12$', payWhat1: '3 גרסאות מלאות של השיר לבחירה',
            payWhat2: 'ניסיון חוזר אחד בחינם, למשל אחרי שינוי שם', payWhat3: 'השתמשו בו כמוזיקה של ההזמנה',
            pay: 'פתיחה תמורת 12$', paying: 'פותחים תשלום מאובטח…',
            pickH: 'השיר שלכם', pickSub: 'האזינו ובחרו את הגרסה שאתם הכי אוהבים.', version: 'גרסה {n}',
            making: 'בהכנה…', use: 'השתמשו כמוזיקה שלי', inUse: 'נוסף להזמנה',
            redo: 'צרו עוד גרסה', redosLeft: 'נותר ניסיון {n}', editFirst: 'קודם ערכו את המילים',
            readyToast: 'השיר שלכם מוכן', open: 'פתיחה', error: 'משהו השתבש. נסו שוב.',
            signIn: 'יש להתחבר כדי ליצור שיר.'
        },
        ar: {
            title: 'أنشئ أغنيتك', close: 'إغلاق',
            occasionOther: "ماذا تحتفلون؟", occasionOtherPh: "مثل: تقاعد، تخرج، حفلة خاصة", lyricsChoose: "كيف تريد كلمات أغنيتك؟", forMe: "✨ اكتبها لي", forMeSub: "يكتب الذكاء الاصطناعي الكلمات من أسمائكم وقصتكم، ويمكنك تعديل كل كلمة.", own: "✍️ سأكتبها بنفسي", ownSub: "اكتب أو الصق كلماتك الخاصة.", ownPh: "[Verse 1]\nسطورك الأولى…\n\n[Chorus]\nالجزء المتكرر…", ownNote: "كلماتك الخاصة فقط: لا يمكن غناء كلمات أغانٍ موجودة. وسوم مثل [Verse] و[Chorus] تساعد المغني لكنها اختيارية.", back: 'رجوع', next: 'التالي', step: 'الخطوة {n} من {t}',
            disclaimer: 'اختياراتك توجّه الملحّن بالذكاء الاصطناعي. كل نسخة تخرج مختلفة قليلًا، لذلك تحصل على 3 نسخ لتختار منها.',
            styleH: 'اختر أسلوبًا', styleSub: 'اضغط ▶ للاستماع. ستكون أغنيتك بهذا الطابع، مع غناء.',
            voiceH: 'من يغني؟', female: 'مطربة', male: 'مطرب', duet: 'ثنائي',
            detailsH: 'أخبرنا', occasion: 'ماذا تحتفلون؟',
            occ: { wedding: 'زفاف', engagement: 'خطوبة', anniversary: 'ذكرى زواج', quinceanera: 'حفلة الخامسة عشرة', birthday: 'عيد ميلاد', coming_of_age: 'البلوغ', baby: 'مولود', other: 'شيء آخر' },
            name1: 'الاسم في الأغنية', name2: 'اسم ثانٍ (اختياري)', sounds: 'كيف يُنطق؟', soundsHint: 'فقط إن كان غير مألوف، مثل "شي-فون"',
            addSound: 'أضف طريقة النطق', date: 'التاريخ (اختياري)', language: 'لغة الأغنية',
            story: 'قصة قصيرة (اختياري)', storyHint: 'كيف تعارفتم، ما تحبونه، تفصيلة عائلية…',
            wish: 'جملة تحب أن تسمعها في الأغنية (اختياري)',
            needName: 'يُرجى إضافة اسم واحد على الأقل.',
            lyricsH: 'كلمات أغنيتك', lyricsSub: 'غيّر أي كلمة. تظهر الأسماء كما كُتبت، ونحن نهتم بنطقها.',
            writing: 'نكتب كلماتك…', romantic: 'أكثر رومانسية', fun: 'أكثر مرحًا', shorter: 'أقصر', rewrite: 'نسخة جديدة',
            previewH: 'استمع إلى أغنيتك', previewSub: 'ننشئ أغنيتك كاملة ونسمعك أول 30 ثانية مجانًا.',
            makePreview: 'أنشئ معاينتي المجانية', previewsLeft: 'تبقّى {n} معاينة مجانية',
            stage1: 'نلحّن الميلودي…', stage2: 'نسجّل الغناء…', stage3: 'نمزج أغنيتك…',
            keepWorking: 'يستغرق ذلك دقيقة تقريبًا. يمكنك الإغلاق ومتابعة التعديل، وسنخبرك.',
            previewLabel: 'معاينة 30 ثانية', changeLyrics: 'تغيير الكلمات', another: 'جرّب معاينة أخرى',
            loveIt: 'أريد الأغنية كاملة', failed: 'لم تنجح هذه. يُرجى المحاولة مجددًا.',
            refused: 'تعذّر غناء بعض الكلمات. أعد صياغة سطر وحاول مجددًا.',
            payH: 'احتفظ بأغنيتك', price: '12 دولارًا', payWhat1: '3 نسخ كاملة من أغنيتك لتختار منها',
            payWhat2: 'إعادة مجانية واحدة، مثلًا بعد تغيير اسم', payWhat3: 'استخدمها موسيقى لدعوتك',
            pay: 'افتح مقابل 12 دولارًا', paying: 'نفتح الدفع الآمن…',
            pickH: 'أغنيتك', pickSub: 'استمع واختر النسخة التي تحبها.', version: 'النسخة {n}',
            making: 'قيد الإنشاء…', use: 'استخدمها موسيقاي', inUse: 'أُضيفت إلى دعوتك',
            redo: 'أنشئ نسخة أخرى', redosLeft: 'تبقّت {n} إعادة', editFirst: 'عدّل الكلمات أولًا',
            readyToast: 'أغنيتك جاهزة', open: 'فتح', error: 'حدث خطأ ما. يُرجى المحاولة مجددًا.',
            signIn: 'سجّل الدخول لإنشاء أغنية.'
        }
    };

    function fmt(s, vars) { return String(s).replace(/\{(\w+)\}/g, function (_, k) { return vars && vars[k] != null ? vars[k] : ''; }); }
    function esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }
    function styleName(id, lang) {
        var lib = (window.QRMusicPicker && window.QRMusicPicker.LIBRARY) || [];
        var t = lib.find(function (x) { return x.id === id; });
        return t ? (t.names[lang] || t.names.en) : id;
    }

    function auth() {
        try { return window.firebaseAuth || (window.firebase && firebase.auth && firebase.auth()); } catch (e) { return null; }
    }
    async function api(method, path, body) {
        var a = auth();
        var user = a && a.currentUser;
        if (!user) throw Object.assign(new Error('signin'), { code: 'signin' });
        var token = await user.getIdToken();
        var res = await fetch('/api/songs' + path, {
            method: method,
            headers: Object.assign({ Authorization: 'Bearer ' + token }, body ? { 'Content-Type': 'application/json' } : {}),
            body: body ? JSON.stringify(body) : undefined
        });
        var data = {};
        try { data = await res.json(); } catch (e) { /* empty */ }
        if (!res.ok || data.success === false) throw Object.assign(new Error(data.error || 'error'), { code: data.code, status: res.status });
        return data;
    }

    // ---- styles --------------------------------------------------------------
    function ensureStyles() {
        if (document.getElementById('qrsc-style')) return;
        var s = document.createElement('style');
        s.id = 'qrsc-style';
        s.textContent = ''
            + '.qrsc{border:0;padding:0;max-width:none;max-height:none;width:100%;height:100%;margin:0;background:#fffdfa;color:#2b2622;'
            + 'font-family:inherit}'
            + '.qrsc::backdrop{background:rgba(20,16,12,.55)}'
            + '@media (min-width:720px){.qrsc{width:620px;height:min(760px,92vh);margin:auto;border-radius:18px;box-shadow:0 24px 70px rgba(0,0,0,.3)}}'
            + '.qrsc-wrap{display:flex;flex-direction:column;height:100%}'
            + '.qrsc-head{display:flex;align-items:center;gap:.75rem;padding:14px 18px 8px}'
            + '.qrsc-head h2{font-size:1.1rem;margin:0;flex:1;font-weight:700}'
            + '.qrsc-x{border:0;background:none;font-size:1.6rem;line-height:1;color:#776d63;cursor:pointer;padding:4px 8px}'
            + '.qrsc-bar{height:4px;background:#efe7dc;margin:0 18px;border-radius:4px;overflow:hidden}'
            + '.qrsc-bar i{display:block;height:100%;background:linear-gradient(90deg,#c9a24a,#b8862b);transition:width .3s}'
            + '.qrsc-stepn{font-size:.75rem;color:#8b8075;padding:6px 18px 0}'
            + '.qrsc-body{flex:1;overflow-y:auto;padding:14px 18px 18px;-webkit-overflow-scrolling:touch}'
            + '.qrsc-body h3{font-size:1.25rem;margin:.2rem 0 .3rem;font-weight:700}'
            + '.qrsc-sub{color:#6f655b;font-size:.92rem;margin:0 0 14px}'
            + '.qrsc-foot{display:flex;gap:10px;align-items:center;padding:12px 18px calc(12px + env(safe-area-inset-bottom,0px));border-top:1px solid #efe7dc;background:#fffdfa}'
            + '.qrsc-foot .sp{flex:1}'
            + '.qrsc-btn{border:0;border-radius:999px;padding:12px 22px;font-weight:600;font-size:.95rem;cursor:pointer;min-height:44px}'
            + '.qrsc-btn.pri{background:#2b2622;color:#fff}.qrsc-btn.pri:disabled{opacity:.5;cursor:default}'
            + '.qrsc-btn.gold{background:linear-gradient(135deg,#d4ad55,#b8862b);color:#fff;box-shadow:0 6px 18px rgba(184,134,43,.35)}'
            + '.qrsc-btn.ghost{background:none;color:#2b2622;padding:12px 8px}'
            + '.qrsc-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}'
            + '@media (min-width:520px){.qrsc-grid{grid-template-columns:repeat(3,minmax(0,1fr))}}'
            + '.qrsc-card{display:flex;align-items:center;gap:8px;border:1.5px solid #e6dccf;border-radius:14px;padding:10px;background:#fff;cursor:pointer;text-align:start;min-height:56px}'
            + '.qrsc-card.on{border-color:#b8862b;background:#fbf4e6;box-shadow:0 0 0 2px rgba(184,134,43,.2)}'
            + '.qrsc-card span{flex:1;font-size:.88rem;font-weight:600;line-height:1.2}'
            + '.qrsc-choice{width:100%;margin:0 0 10px;padding:14px 16px;min-height:72px}'
            + '.qrsc-choice b{display:block;font-size:1rem;margin-bottom:3px}.qrsc-choice small{display:block;color:#6f655b;font-size:.85rem;font-weight:400;line-height:1.35}'
            + '.qrsc-play{flex:none;width:34px;height:34px;border-radius:50%;border:0;background:#f1e8db;color:#2b2622;cursor:pointer;font-size:.8rem}'
            + '.qrsc-seg{display:flex;gap:8px;flex-wrap:wrap;margin:6px 0 4px}'
            + '.qrsc-seg button{border:1.5px solid #e6dccf;background:#fff;border-radius:999px;padding:9px 16px;font-weight:600;cursor:pointer;min-height:42px}'
            + '.qrsc-seg button.on{border-color:#b8862b;background:#fbf4e6}'
            + '.qrsc-h4{font-size:.95rem;font-weight:700;margin:18px 0 6px}'
            + '.qrsc-field{margin:0 0 12px}.qrsc-field label{display:block;font-size:.85rem;font-weight:600;margin-bottom:4px}'
            + '.qrsc-field input,.qrsc-field select,.qrsc-field textarea{width:100%;border:1.5px solid #e1d7ca;border-radius:12px;padding:10px 12px;font-size:16px;background:#fff;color:inherit;font-family:inherit}'
            + '.qrsc-field textarea{resize:vertical}'
            + '.qrsc-hint{font-size:.78rem;color:#8b8075;margin-top:3px}'
            + '.qrsc-link{border:0;background:none;color:#8a6417;font-weight:600;padding:2px 0;cursor:pointer;font-size:.85rem}'
            + '.qrsc-lyrics{font-family:inherit;line-height:1.55;min-height:300px}'
            + '.qrsc-chips{display:flex;gap:8px;flex-wrap:wrap;margin:8px 0}'
            + '.qrsc-chips button{border:1.5px solid #e6dccf;background:#fff;border-radius:999px;padding:7px 13px;font-size:.85rem;cursor:pointer}'
            + '.qrsc-note{font-size:.8rem;color:#7c7166;background:#f7f1e8;border-radius:10px;padding:9px 11px;margin-top:12px}'
            + '.qrsc-err{color:#b02a37;font-size:.88rem;margin-top:10px}'
            + '.qrsc-prog{height:10px;background:#efe7dc;border-radius:10px;overflow:hidden;margin:16px 0 8px}'
            + '.qrsc-prog i{display:block;height:100%;background:linear-gradient(90deg,#d4ad55,#b8862b);transition:width 1s linear}'
            + '.qrsc-stage{font-weight:600;margin:0}'
            + '.qrsc-player{width:100%;margin:10px 0}'
            + '.qrsc-price{font-size:2rem;font-weight:800;margin:4px 0 10px}'
            + '.qrsc-list{list-style:none;padding:0;margin:0 0 14px}.qrsc-list li{padding:6px 0 6px 26px;position:relative}'
            + '.qrsc-list li:before{content:"✓";position:absolute;inset-inline-start:0;color:#b8862b;font-weight:800}'
            + '[dir="rtl"] .qrsc-list li{padding:6px 26px 6px 0}'
            + '.qrsc-ver{border:1.5px solid #e6dccf;border-radius:14px;padding:12px;margin-bottom:10px;background:#fff}'
            + '.qrsc-ver b{display:block;margin-bottom:4px}'
            + '.qrsc-spin{display:inline-block;width:16px;height:16px;border:2px solid #d9cbb5;border-top-color:#b8862b;border-radius:50%;animation:qrsc-r .8s linear infinite;vertical-align:-3px;margin-inline-end:6px}'
            + '@keyframes qrsc-r{to{transform:rotate(360deg)}}'
            + '.qrsc-toast{position:fixed;z-index:2147482000;inset-inline-end:16px;bottom:calc(76px + env(safe-area-inset-bottom,0px));background:#2b2622;color:#fff;border-radius:14px;padding:12px 14px;display:flex;gap:12px;align-items:center;box-shadow:0 10px 30px rgba(0,0,0,.25)}'
            + '.qrsc-toast button{border:0;border-radius:999px;background:#d4ad55;color:#2b2622;font-weight:700;padding:7px 14px;cursor:pointer}';
        document.head.appendChild(s);
    }

    // ---- the creator ---------------------------------------------------------
    var current = null; // only one creator at a time

    function Creator(opts) {
        this.opts = opts || {};
        var l = String(this.opts.lang || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
        this.lang = T[l] ? l : 'en';
        this.t = T[this.lang];
        this.project = null;
        this.step = 0;
        this.inputs = {
            style: 'romantic-piano', voice: 'female', language: LANGS.indexOf(l) >= 0 ? l : 'en', occasion: 'wedding',
            names: [{ name: '', sounds: '' }, { name: '', sounds: '' }], date: '', story: '', wish: ''
        };
        this.lyricsDirty = false;
        this.busy = '';
        this.error = '';
        this.audio = null;
        this.playing = null;
        this.poll = null;
        this.progStart = 0;
    }

    Creator.prototype.mount = function () {
        ensureStyles();
        var d = document.createElement('dialog');
        d.className = 'qrsc';
        d.setAttribute('aria-labelledby', 'qrsc-title');
        d.dir = document.documentElement.dir || (this.lang === 'he' || this.lang === 'ar' ? 'rtl' : 'ltr');
        document.body.appendChild(d);
        this.el = d;
        this.closed = false;
        var self = this;
        // Escape closes the dialog natively; our own buttons go through close().
        d.addEventListener('close', function () { self.onClose(); });
        d.addEventListener('click', function (e) { self.onClick(e); });
        d.addEventListener('input', function (e) { self.onInput(e); });
        d.addEventListener('change', function (e) { self.onInput(e); });
        this.render();
        d.showModal();
    };

    Creator.prototype.totalSteps = function () { return 6; };

    Creator.prototype.render = function () {
        var t = this.t, n = this.step + 1, total = this.totalSteps();
        var body = [this.renderStyle, this.renderDetails, this.renderLyrics, this.renderPreview, this.renderPay, this.renderPick][this.step].call(this);
        // Keep the textarea's caret and scroll when re-rendering around it.
        var ta = this.el.querySelector('.qrsc-lyrics');
        var keep = ta && document.activeElement === ta ? { s: ta.selectionStart, e: ta.selectionEnd, top: ta.scrollTop } : null;
        // Every click redraws the whole popup, which threw the body back to its
        // top (picking a style further down made it jump). Same step: keep the
        // scroll and the focused control. New step: start at the top.
        var sameStep = this.renderedStep === this.step;
        var oldBody = this.el.querySelector('.qrsc-body');
        // Audio players survive a redraw: the 4 s progress check redraws the
        // popup, and replacing the <audio> stopped a version the host was
        // listening to. Moved back in within the same task, so playback runs on.
        var oldAudio = {};
        var olds = this.el.querySelectorAll('audio[src]');
        for (var oi = 0; oi < olds.length; oi++) oldAudio[olds[oi].getAttribute('src')] = olds[oi];
        var bodyTop = sameStep && oldBody ? oldBody.scrollTop : 0;
        var f = document.activeElement && this.el.contains(document.activeElement) ? document.activeElement : null;
        var focusSel = sameStep && f && f !== ta ? ['data-style', 'data-play', 'data-voice', 'data-tweak', 'data-use', 'data-a', 'data-f'].map(function (k) {
            var v = f.getAttribute(k); return v != null ? '[' + k + '="' + v + '"]' : '';
        }).join('') : '';
        this.el.innerHTML = ''
            + '<div class="qrsc-wrap">'
            + '<div class="qrsc-head"><h2 id="qrsc-title">♪ ' + esc(t.title) + '</h2>'
            + '<button type="button" class="qrsc-x" data-a="close" aria-label="' + esc(t.close) + '">×</button></div>'
            + '<div class="qrsc-bar" aria-hidden="true"><i style="width:' + Math.round(n / total * 100) + '%"></i></div>'
            + '<div class="qrsc-stepn">' + esc(fmt(t.step, { n: n, t: total })) + '</div>'
            + '<div class="qrsc-body">' + body.html + (this.error ? '<div class="qrsc-err" role="alert">' + esc(this.error) + '</div>' : '') + '</div>'
            + (body.foot ? '<div class="qrsc-foot">' + body.foot + '</div>' : '')
            + '</div>';
        this.renderedStep = this.step;
        var news = this.el.querySelectorAll('audio[src]');
        for (var ni = 0; ni < news.length; ni++) {
            var keepPlayer = oldAudio[news[ni].getAttribute('src')];
            if (keepPlayer && keepPlayer !== news[ni]) news[ni].parentNode.replaceChild(keepPlayer, news[ni]);
        }
        var newBody = this.el.querySelector('.qrsc-body');
        if (newBody) newBody.scrollTop = bodyTop;
        if (focusSel) {
            var again = null;
            try { again = this.el.querySelector(focusSel); } catch (e) { /* odd value */ }
            if (again) { try { again.focus({ preventScroll: true }); } catch (e) { again.focus(); } }
        }
        if (keep) {
            var nta = this.el.querySelector('.qrsc-lyrics');
            if (nta) { try { nta.focus({ preventScroll: true }); } catch (e) { nta.focus(); } nta.setSelectionRange(keep.s, keep.e); nta.scrollTop = keep.top; }
        }
    };

    Creator.prototype.backBtn = function () { return '<button type="button" class="qrsc-btn ghost" data-a="back">' + (document.documentElement.dir === 'rtl' ? '→ ' : '← ') + esc(this.t.back) + '</button>'; };

    Creator.prototype.renderStyle = function () {
        var t = this.t, self = this;
        var cards = STYLE_IDS.map(function (id) {
            var on = self.inputs.style === id;
            var playing = self.playing === id;
            return '<div class="qrsc-card' + (on ? ' on' : '') + '" data-style="' + id + '" role="radio" aria-checked="' + on + '" tabindex="0">'
                + '<button type="button" class="qrsc-play" data-play="' + id + '" aria-label="▶ ' + esc(styleName(id, self.lang)) + '">' + (playing ? '■' : '▶') + '</button>'
                + '<span>' + esc(styleName(id, self.lang)) + '</span></div>';
        }).join('');
        var seg = ['female', 'male', 'duet'].map(function (v) {
            return '<button type="button" data-voice="' + v + '" class="' + (self.inputs.voice === v ? 'on' : '') + '" aria-pressed="' + (self.inputs.voice === v) + '">' + esc(t[v]) + '</button>';
        }).join('');
        return {
            html: '<h3>' + esc(t.styleH) + '</h3><p class="qrsc-sub">' + esc(t.styleSub) + '</p>'
                + '<div class="qrsc-grid" role="radiogroup">' + cards + '</div>'
                + '<div class="qrsc-h4">' + esc(t.voiceH) + '</div><div class="qrsc-seg">' + seg + '</div>'
                + '<div class="qrsc-note">' + esc(t.disclaimer) + '</div>',
            foot: '<span class="sp"></span><button type="button" class="qrsc-btn pri" data-a="next">' + esc(t.next) + '</button>'
        };
    };

    Creator.prototype.renderDetails = function () {
        var t = this.t, i = this.inputs, self = this;
        var occ = OCCASIONS.map(function (o) { return '<option value="' + o + '"' + (i.occasion === o ? ' selected' : '') + '>' + esc(t.occ[o]) + '</option>'; }).join('');
        var langs = LANGS.map(function (l) { return '<option value="' + l + '"' + (i.language === l ? ' selected' : '') + '>' + esc(LANG_NAMES[l]) + '</option>'; }).join('');
        function nameField(idx, label) {
            var n = i.names[idx];
            var showSound = n.sounds || self['showSound' + idx];
            return '<div class="qrsc-field"><label for="qrsc-n' + idx + '">' + esc(label) + '</label>'
                + '<input id="qrsc-n' + idx + '" data-f="name' + idx + '" value="' + esc(n.name) + '" maxlength="40" dir="auto" autocomplete="off">'
                + (showSound
                    ? '<div class="qrsc-field" style="margin:8px 0 0"><label for="qrsc-s' + idx + '">' + esc(t.sounds) + '</label>'
                    + '<input id="qrsc-s' + idx + '" data-f="sounds' + idx + '" value="' + esc(n.sounds) + '" maxlength="60" dir="auto" autocomplete="off">'
                    + '<div class="qrsc-hint">' + esc(t.soundsHint) + '</div></div>'
                    : '<button type="button" class="qrsc-link" data-a="sound' + idx + '">+ ' + esc(t.addSound) + '</button>')
                + '</div>';
        }
        return {
            html: '<h3>' + esc(t.detailsH) + '</h3>'
                + '<div class="qrsc-field"><label for="qrsc-occ">' + esc(t.occasion) + '</label><select id="qrsc-occ" data-f="occasion">' + occ + '</select></div>'
                + (i.occasion === 'other' ? '<div class="qrsc-field"><label for="qrsc-occo">' + esc(t.occasionOther) + '</label><input id="qrsc-occo" data-f="occasionOther" value="' + esc(i.occasionOther || '') + '" maxlength="60" dir="auto" placeholder="' + esc(t.occasionOtherPh) + '"></div>' : '')
                + nameField(0, t.name1) + nameField(1, t.name2)
                + '<div class="qrsc-field"><label for="qrsc-lang">' + esc(t.language) + '</label><select id="qrsc-lang" data-f="language">' + langs + '</select></div>'
                + '<div class="qrsc-field"><label for="qrsc-date">' + esc(t.date) + '</label><input id="qrsc-date" data-f="date" value="' + esc(i.date) + '" maxlength="60" dir="auto"></div>'
                + '<div class="qrsc-field"><label for="qrsc-story">' + esc(t.story) + '</label><textarea id="qrsc-story" data-f="story" rows="3" maxlength="600" dir="auto" placeholder="' + esc(t.storyHint) + '">' + esc(i.story) + '</textarea></div>'
                + '<div class="qrsc-field"><label for="qrsc-wish">' + esc(t.wish) + '</label><input id="qrsc-wish" data-f="wish" value="' + esc(i.wish) + '" maxlength="160" dir="auto"></div>',
            foot: this.backBtn() + '<span class="sp"></span><button type="button" class="qrsc-btn pri" data-a="next"' + (this.busy ? ' disabled' : '') + '>'
                + (this.busy ? '<span class="qrsc-spin"></span>' : '') + esc(t.next) + '</button>'
        };
    };

    Creator.prototype.renderLyrics = function () {
        var t = this.t, p = this.project || {};
        var writing = this.busy === 'lyrics';
        if (!p.lyrics && !writing && !this.ownMode) {
            return {
                html: '<h3>' + esc(t.lyricsChoose) + '</h3>'
                    + '<button type="button" class="qrsc-card qrsc-choice" data-a="draft"><span><b>' + esc(t.forMe) + '</b><small>' + esc(t.forMeSub) + '</small></span></button>'
                    + '<button type="button" class="qrsc-card qrsc-choice" data-a="own"><span><b>' + esc(t.own) + '</b><small>' + esc(t.ownSub) + '</small></span></button>'
                    + '<div class="qrsc-note">' + esc(t.disclaimer) + '</div>',
                foot: this.backBtn() + '<span class="sp"></span>'
            };
        }
        var chips = ['romantic', 'fun', 'shorter', 'rewrite'].map(function (k) {
            return '<button type="button" data-tweak="' + k + '"' + (writing ? ' disabled' : '') + '>' + esc(t[k]) + '</button>';
        }).join('');
        return {
            html: '<h3>' + esc(t.lyricsH) + (p.title ? ': <span dir="auto">' + esc(p.title) + '</span>' : '') + '</h3>'
                + '<p class="qrsc-sub">' + esc(t.lyricsSub) + '</p>'
                + (writing && !p.lyrics ? '<p><span class="qrsc-spin"></span>' + esc(t.writing) + '</p>'
                    : '<div class="qrsc-field"><textarea class="qrsc-lyrics" data-f="lyrics" dir="auto" rows="14" maxlength="1400" placeholder="' + esc(t.ownPh) + '"' + (writing ? ' disabled' : '') + '>' + esc(p.lyrics || '') + '</textarea></div>'
                    + (this.ownMode ? '<div class="qrsc-hint">' + esc(t.ownNote) + '</div>' : '')
                    + (p.lyrics ? '<div class="qrsc-chips">' + chips + (writing ? '<span><span class="qrsc-spin"></span>' + esc(t.writing) + '</span>' : '') + '</div>' : ''))
                + '<div class="qrsc-note">' + esc(t.disclaimer) + '</div>',
            foot: this.backBtn() + '<span class="sp"></span><button type="button" class="qrsc-btn pri" data-a="next" data-needs-lyrics' + (writing || !(p.lyrics || '').trim() || this.busy ? ' disabled' : '') + '>' + esc(t.next) + '</button>'
        };
    };

    Creator.prototype.latestPreview = function () {
        var songs = (this.project && this.project.songs) || [];
        var pv = songs.filter(function (s) { return s.kind === 'preview'; });
        return pv[pv.length - 1] || null;
    };

    Creator.prototype.renderPreview = function () {
        var t = this.t, p = this.project || {};
        var s = this.latestPreview();
        var html = '<h3>' + esc(t.previewH) + '</h3><p class="qrsc-sub">' + esc(t.previewSub) + '</p>';
        var foot = this.backBtn() + '<span class="sp"></span>';
        if (s && s.status === 'generating') {
            var pct = Math.min(95, Math.round((Date.now() - (this.progStart || Date.now())) / 800));
            var stage = pct < 35 ? t.stage1 : pct < 75 ? t.stage2 : t.stage3;
            html += '<div class="qrsc-prog" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + pct + '"><i style="width:' + pct + '%"></i></div>'
                + '<p class="qrsc-stage">' + esc(stage) + '</p><p class="qrsc-sub" style="margin-top:6px">' + esc(t.keepWorking) + '</p>';
        } else if (s && s.status === 'ready' && s.previewUrl) {
            html += '<div class="qrsc-h4">' + esc(t.previewLabel) + '</div>'
                + '<audio class="qrsc-player" controls preload="auto" src="' + esc(s.previewUrl) + '"></audio>'
                + '<div class="qrsc-chips"><button type="button" data-a="tolyrics">' + esc(t.changeLyrics) + '</button>'
                + (p.previewsLeft > 0 ? '<button type="button" data-a="preview">' + esc(t.another) + ' (' + p.previewsLeft + ')</button>' : '') + '</div>';
            foot += '<button type="button" class="qrsc-btn gold" data-a="next">' + esc(t.loveIt) + '</button>';
        } else {
            if (s && s.status === 'failed') html += '<div class="qrsc-err" role="alert">' + esc(s.error === 'refused' ? t.refused : t.failed) + '</div>';
            html += '<p style="margin-top:18px"><button type="button" class="qrsc-btn gold" data-a="preview"' + (this.busy || !p.previewsLeft ? ' disabled' : '') + '>'
                + (this.busy === 'preview' ? '<span class="qrsc-spin"></span>' : '') + '♪ ' + esc(t.makePreview) + '</button></p>'
                + '<div class="qrsc-hint">' + esc(fmt(t.previewsLeft, { n: p.previewsLeft != null ? p.previewsLeft : 2 })) + '</div>';
        }
        return { html: html, foot: foot };
    };

    Creator.prototype.renderPay = function () {
        var t = this.t;
        return {
            html: '<h3>' + esc(t.payH) + '</h3><div class="qrsc-price">' + esc(t.price) + '</div>'
                + '<ul class="qrsc-list"><li>' + esc(t.payWhat1) + '</li><li>' + esc(t.payWhat2) + '</li><li>' + esc(t.payWhat3) + '</li></ul>'
                + '<div class="qrsc-note">' + esc(t.disclaimer) + '</div>',
            foot: this.backBtn() + '<span class="sp"></span><button type="button" class="qrsc-btn gold" data-a="pay"' + (this.busy ? ' disabled' : '') + '>'
                + (this.busy === 'pay' ? '<span class="qrsc-spin"></span>' + esc(t.paying) : esc(t.pay)) + '</button>'
        };
    };

    Creator.prototype.renderPick = function () {
        var t = this.t, p = this.project || {}, self = this;
        var used = this.opts.currentUrl || '';
        var rows = (p.songs || []).filter(function (s) { return s.status !== 'failed' || s.kind === 'version'; }).map(function (s, idx) {
            var head = '<b>' + esc(fmt(t.version, { n: idx + 1 })) + '</b>';
            if (s.status === 'generating') return '<div class="qrsc-ver">' + head + '<span class="qrsc-spin"></span>' + esc(t.making) + '</div>';
            if (s.status === 'failed') return '<div class="qrsc-ver">' + head + '<span class="qrsc-err">' + esc(t.failed) + '</span></div>';
            var inUse = s.url && used === s.url;
            return '<div class="qrsc-ver">' + head
                + '<audio class="qrsc-player" controls preload="none" src="' + esc(s.url || s.previewUrl || '') + '"></audio>'
                + (s.url ? '<button type="button" class="qrsc-btn ' + (inUse ? 'ghost' : 'pri') + '" data-use="' + esc(s.id) + '"' + (inUse ? ' disabled' : '') + '>'
                    + (inUse ? '✓ ' + esc(t.inUse) : esc(t.use)) + '</button>' : '')
                + '</div>';
        }).join('');
        var canRedo = p.redosLeft > 0 && !p.generating;
        return {
            html: '<h3>' + esc(t.pickH) + (p.title ? ': <span dir="auto">' + esc(p.title) + '</span>' : '') + '</h3><p class="qrsc-sub">' + esc(t.pickSub) + '</p>' + rows
                + (p.redosLeft > 0 ? '<div class="qrsc-chips"><button type="button" data-a="redo"' + (canRedo ? '' : ' disabled') + '>' + esc(t.redo) + ' · ' + esc(fmt(t.redosLeft, { n: p.redosLeft })) + '</button>'
                    + '<button type="button" data-a="tolyrics">' + esc(t.editFirst) + '</button></div>' : '')
                + '<div class="qrsc-note">' + esc(t.disclaimer) + '</div>',
            foot: null
        };
    };

    // ---- events --------------------------------------------------------------
    Creator.prototype.onInput = function (e) {
        var f = e.target.getAttribute && e.target.getAttribute('data-f');
        if (!f) return;
        var v = e.target.value;
        var m = /^(name|sounds)(\d)$/.exec(f);
        if (m) this.inputs.names[+m[2]][m[1] === 'name' ? 'name' : 'sounds'] = v;
        else if (f === 'lyrics') {
            if (this.project) this.project.lyrics = v;
            this.lyricsDirty = true;
            var nb = this.el.querySelector('[data-needs-lyrics]');
            if (nb && !this.busy) nb.disabled = !v.trim();
        }
        else {
            this.inputs[f] = v;
            if (f === 'occasion' && e.type === 'change') this.render();
        }
        if (this.error && e.type === 'input') { this.error = ''; var er = this.el.querySelector('.qrsc-err'); if (er) er.remove(); }
    };

    Creator.prototype.onClick = function (e) {
        if (e.target === this.el) { this.close(); return; } // backdrop
        var b = e.target.closest('button, [data-style]');
        if (!b) return;
        var self = this;
        if (b.hasAttribute('data-play')) { e.stopPropagation(); this.togglePlay(b.getAttribute('data-play')); return; }
        if (b.hasAttribute('data-style')) { this.inputs.style = b.getAttribute('data-style'); this.render(); return; }
        if (b.hasAttribute('data-voice')) { this.inputs.voice = b.getAttribute('data-voice'); this.render(); return; }
        if (b.hasAttribute('data-tweak')) { this.tweak(b.getAttribute('data-tweak')); return; }
        if (b.hasAttribute('data-use')) { this.use(b.getAttribute('data-use')); return; }
        var a = b.getAttribute('data-a');
        if (a === 'close') this.close();
        else if (a === 'back') { this.stopPlay(); this.step = Math.max(0, this.step - (this.step === 5 ? 3 : 1)); this.error = ''; this.render(); }
        else if (a === 'next') this.next();
        else if (a === 'sound0' || a === 'sound1') { this['showSound' + a.slice(-1)] = true; this.render(); var el = this.el.querySelector('#qrsc-s' + a.slice(-1)); if (el) el.focus(); }
        else if (a === 'preview') this.preview();
        else if (a === 'tolyrics') { this.step = 2; this.render(); }
        else if (a === 'draft') { this.ownMode = false; this.tweak(''); }
        else if (a === 'own') { this.ownMode = true; this.render(); var ta = this.el.querySelector('.qrsc-lyrics'); if (ta) ta.focus(); }
        else if (a === 'pay') this.pay();
        else if (a === 'redo') this.redo();
        void self;
    };

    Creator.prototype.togglePlay = function (id) {
        if (this.playing === id) { this.stopPlay(); this.render(); return; }
        this.stopPlay();
        this.audio = new Audio('/assets/audio/music/' + id + '.mp3');
        this.playing = id;
        var self = this;
        this.audio.addEventListener('ended', function () { self.playing = null; self.render(); });
        var pr = this.audio.play();
        if (pr && pr.catch) pr.catch(function () { self.playing = null; self.render(); });
        this.render();
    };
    Creator.prototype.stopPlay = function () {
        if (this.audio) { try { this.audio.pause(); } catch (e) { /* ignore */ } }
        this.audio = null; this.playing = null;
    };

    Creator.prototype.fail = function (err) {
        this.busy = '';
        this.error = err && err.code === 'signin' ? this.t.signIn : (err && err.message && err.message !== 'error' ? err.message : this.t.error);
        this.render();
    };

    Creator.prototype.setProject = function (proj) {
        this.project = proj;
        if (proj && proj.inputs) {
            var n = proj.inputs.names || [];
            this.inputs = Object.assign({}, this.inputs, proj.inputs, {
                names: [n[0] || { name: '', sounds: '' }, n[1] || { name: '', sounds: '' }]
            });
        }
        this.watch();
    };

    Creator.prototype.payloadInputs = function () {
        var i = this.inputs;
        return Object.assign({}, i, { names: i.names.filter(function (n) { return n.name && n.name.trim(); }) });
    };

    Creator.prototype.next = async function () {
        this.stopPlay();
        this.error = '';
        try {
            if (this.step === 0) { this.step = 1; this.render(); return; }
            if (this.step === 1) {
                if (!this.inputs.names[0].name.trim() && !this.inputs.names[1].name.trim()) { this.error = this.t.needName; this.render(); return; }
                this.busy = 'save'; this.render();
                if (!this.project) {
                    this.setProject((await api('POST', '/projects', { context: this.opts.context || {}, inputs: this.payloadInputs() })).project);
                } else {
                    this.setProject((await api('PUT', '/projects/' + this.project.id + '/inputs', { inputs: this.payloadInputs() })).project);
                }
                this.busy = ''; this.step = 2; this.render();
                return;
            }
            if (this.step === 2) {
                if (this.lyricsDirty) {
                    this.busy = 'save'; this.render();
                    this.setProject((await api('PUT', '/projects/' + this.project.id + '/lyrics', { lyrics: this.project.lyrics })).project);
                    this.lyricsDirty = false;
                }
                this.busy = ''; this.step = this.project.paid ? 5 : 3; this.render();
                return;
            }
            if (this.step === 3) { this.step = 4; this.render(); return; }
        } catch (err) { this.fail(err); }
    };

    Creator.prototype.tweak = async function (k) {
        if (!this.project) return;
        this.busy = 'lyrics'; this.error = ''; this.render();
        try {
            var body = k ? { tweak: k, lyrics: this.project.lyrics } : {};
            this.setProject((await api('POST', '/projects/' + this.project.id + '/lyrics', body)).project);
            this.lyricsDirty = false;
            this.busy = ''; this.render();
        } catch (err) { this.fail(err); }
    };

    Creator.prototype.preview = async function () {
        this.busy = 'preview'; this.error = ''; this.render();
        try {
            if (this.lyricsDirty) {
                this.setProject((await api('PUT', '/projects/' + this.project.id + '/lyrics', { lyrics: this.project.lyrics })).project);
                this.lyricsDirty = false;
            }
            this.progStart = Date.now();
            this.setProject((await api('POST', '/projects/' + this.project.id + '/preview')).project);
            this.busy = ''; this.render();
        } catch (err) { this.fail(err); }
    };

    Creator.prototype.pay = async function () {
        this.busy = 'pay'; this.render();
        try {
            var country = '';
            try { country = (window.QRGeo && window.QRGeo.country) || document.documentElement.dataset.country || ''; } catch (e) { /* none */ }
            var u = new URL(location.href);
            u.searchParams.delete('songProject'); u.searchParams.delete('song');
            var res = await api('POST', '/projects/' + this.project.id + '/checkout', { returnUrl: u.pathname + u.search, ipCountry: country || null, origin: location.origin });
            location.href = res.url;
        } catch (err) { this.fail(err); }
    };

    Creator.prototype.redo = async function () {
        try { this.setProject((await api('POST', '/projects/' + this.project.id + '/redo')).project); this.render(); }
        catch (err) { this.fail(err); }
    };

    Creator.prototype.use = function (songId) {
        var s = (this.project.songs || []).find(function (x) { return x.id === songId; });
        if (!s || !s.url) return;
        this.opts.currentUrl = s.url;
        if (typeof this.opts.onUse === 'function') {
            this.opts.onUse({ enabled: true, url: s.url, name: this.project.title || 'My song', source: 'generated', trackId: this.project.id, rightsConfirmedAt: null });
        }
        this.render();
    };

    function songsSignature(p) {
        return ((p && p.songs) || []).map(function (s) { return s.id + ':' + s.status + ':' + (s.url ? 1 : 0); }).join('|') + (p && p.redosLeft);
    }

    /** Keep checking while anything is being made, even with the popup closed. */
    Creator.prototype.watch = function () {
        var self = this;
        clearTimeout(this.poll);
        if (!this.project || !this.project.generating) return;
        if (!this.progStart) this.progStart = Date.now();
        this.poll = setTimeout(async function () {
            try {
                var wasGenerating = true;
                var before = songsSignature(self.project);
                var proj = (await api('GET', '/projects/' + self.project.id)).project;
                self.setProject(proj);
                if (self.el && self.el.open) {
                    if (!proj.generating) self.progStart = 0;
                    // Do not wipe the host's typing on the lyrics step. The
                    // preview step redraws each check for its progress bar; the
                    // pick step only when a version actually changed.
                    var changed = songsSignature(proj) !== before;
                    if (self.step === 3 || (self.step !== 2 && changed)) self.render();
                } else if (wasGenerating && !proj.generating) {
                    self.toast();
                }
            } catch (e) { self.poll = setTimeout(function () { self.watch(); }, 8000); }
        }, this.el && this.el.open ? 4000 : 8000);
    };

    Creator.prototype.toast = function () {
        var t = this.t, self = this;
        var old = document.querySelector('.qrsc-toast');
        if (old) old.remove();
        ensureStyles();
        var d = document.createElement('div');
        d.className = 'qrsc-toast';
        d.setAttribute('role', 'status');
        d.innerHTML = '<span>♪ ' + esc(t.readyToast) + '</span><button type="button">' + esc(t.open) + '</button>';
        d.querySelector('button').addEventListener('click', function () { d.remove(); self.reopen(); });
        document.body.appendChild(d);
        setTimeout(function () { if (d.parentNode) d.remove(); }, 15000);
    };

    Creator.prototype.reopen = function () {
        if (!this.el || !document.body.contains(this.el)) this.mount();
        else if (!this.el.open) { this.render(); this.el.showModal(); }
        current = this;
    };

    /**
     * Close and clean up now. The dialog's own close event is queued as a task,
     * and a backgrounded page can hold it back; cleanup must not depend on it.
     */
    Creator.prototype.close = function () {
        if (this.el && this.el.open) this.el.close();
        this.onClose();
    };

    Creator.prototype.onClose = function () {
        if (this.closed) return;
        this.closed = true;
        this.stopPlay();
        var players = this.el ? this.el.querySelectorAll('audio') : [];
        for (var i = 0; i < players.length; i++) { try { players[i].pause(); } catch (e) { /* ignore */ } }
        // Remove the closed dialog: every open builds a fresh one, and reopen()
        // mounts again when the element is gone.
        if (this.el && this.el.parentNode) this.el.parentNode.removeChild(this.el);
        this.watch();
        if (typeof this.opts.onClose === 'function') this.opts.onClose();
    };

    /** Where to resume a saved song. */
    Creator.prototype.resumeStep = function () {
        var p = this.project;
        if (!p) return 0;
        if (p.paid) return 5;
        if ((p.songs || []).some(function (s) { return s.kind === 'preview'; })) return 3;
        if (p.lyrics) return 2;
        return 1;
    };

    async function open(opts) {
        if (current && current.el && current.el.open) return current;
        var c = new Creator(opts);
        current = c;
        try {
            var res = null;
            if (opts && opts.projectId && opts.confirmPayment) res = await api('POST', '/projects/' + encodeURIComponent(opts.projectId) + '/confirm');
            else if (opts && opts.projectId) res = await api('GET', '/projects/' + encodeURIComponent(opts.projectId));
            else if (opts && opts.context && opts.context.targetId) res = await api('GET', '/projects/latest?targetId=' + encodeURIComponent(opts.context.targetId));
            if (res && res.project) { c.setProject(res.project); c.step = c.resumeStep(); }
        } catch (e) { /* start fresh */ }
        c.mount();
        return c;
    }

    window.QRSongCreator = { open: open, T: T };
})();
